import React from 'react';
import { Trophy, Crown, Medal, Star } from 'lucide-react';
import type { LeaderboardEntry, LoyaltyStatus } from '../../data/go2LegendeMock';

interface LeaderboardProps {
  entries: LeaderboardEntry[];
}

const getLevelBadgeColor = (level: LoyaltyStatus): string => {
  switch (level) {
    case 'Legend':
      return 'bg-amber-500 text-white';
    case 'World Traveler':
      return 'bg-go2-blue text-white';
    case 'Traveler':
      return 'bg-gray-500 text-white';
  }
};

const getLevelLabel = (level: LoyaltyStatus): string => {
  switch (level) {
    case 'Legend':
      return 'Legenda';
    case 'World Traveler':
      return 'Svetski Putnik';
    case 'Traveler':
      return 'Putnik';
  }
};

const getAvatarColor = (level: LoyaltyStatus): string => {
  switch (level) {
    case 'Legend':
      return 'bg-amber-100 text-amber-700 border-amber-300';
    case 'World Traveler':
      return 'bg-blue-100 text-go2-blue border-blue-300';
    case 'Traveler':
      return 'bg-gray-100 text-gray-700 border-gray-300';
  }
};

export const Leaderboard: React.FC<LeaderboardProps> = ({ entries }) => {
  const topThree = entries.slice(0, 3);
  const restOfList = entries.slice(3, 10);
  const currentUserRank = entries.find(e => e.isCurrentUser)?.rank || 6;

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-go2-pink to-brand-600 p-6 text-center text-white">
        <div className="flex items-center justify-center gap-2 mb-2">
          <Trophy className="w-8 h-8" />
          <h2 className="text-2xl font-bold">Mesečna Rang Lista</h2>
        </div>
        <p className="text-brand-100">Februar 2026</p>
      </div>

      {/* Top 3 Podium */}
      <div className="p-6 bg-gradient-to-b from-brand-50 to-white">
        <div className="grid grid-cols-3 gap-4 max-w-3xl mx-auto">
          {/* Rank 2 - Silver */}
          {topThree[1] && (
            <div className="flex flex-col items-center pt-8">
              <Medal className="w-8 h-8 text-gray-400 mb-2" />
              <div className="bg-white rounded-lg border-4 border-gray-300 p-4 text-center shadow-md w-full">
                <div className={`w-16 h-16 rounded-full ${getAvatarColor(topThree[1].level)} border-4 flex items-center justify-center text-xl font-bold mx-auto mb-2`}>
                  {topThree[1].initials}
                </div>
                <p className="font-bold text-gray-900 mb-1">{topThree[1].name}</p>
                <p className="text-2xl font-bold text-gray-600 mb-2">{topThree[1].points.toLocaleString()}</p>
                <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${getLevelBadgeColor(topThree[1].level)}`}>
                  {getLevelLabel(topThree[1].level)}
                </span>
                <p className="text-xs text-gray-500 mt-2">{topThree[1].trips} putovanja</p>
              </div>
            </div>
          )}

          {/* Rank 1 - Gold */}
          {topThree[0] && (
            <div className="flex flex-col items-center">
              <Crown className="w-10 h-10 text-amber-500 mb-2" />
              <div className="bg-white rounded-lg border-4 border-amber-500 p-4 text-center shadow-xl w-full">
                <div className={`w-20 h-20 rounded-full ${getAvatarColor(topThree[0].level)} border-4 flex items-center justify-center text-2xl font-bold mx-auto mb-2`}>
                  {topThree[0].initials}
                </div>
                <p className="font-bold text-gray-900 mb-1 text-lg">{topThree[0].name}</p>
                <p className="text-3xl font-bold text-amber-600 mb-2">{topThree[0].points.toLocaleString()}</p>
                <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${getLevelBadgeColor(topThree[0].level)}`}>
                  {getLevelLabel(topThree[0].level)}
                </span>
                <p className="text-xs text-gray-500 mt-2">{topThree[0].trips} putovanja</p>
              </div>
            </div>
          )}

          {/* Rank 3 - Bronze */}
          {topThree[2] && (
            <div className="flex flex-col items-center pt-8">
              <Medal className="w-8 h-8 text-orange-500 mb-2" />
              <div className="bg-white rounded-lg border-4 border-orange-400 p-4 text-center shadow-md w-full">
                <div className={`w-16 h-16 rounded-full ${getAvatarColor(topThree[2].level)} border-4 flex items-center justify-center text-xl font-bold mx-auto mb-2`}>
                  {topThree[2].initials}
                </div>
                <p className="font-bold text-gray-900 mb-1">{topThree[2].name}</p>
                <p className="text-2xl font-bold text-orange-600 mb-2">{topThree[2].points.toLocaleString()}</p>
                <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${getLevelBadgeColor(topThree[2].level)}`}>
                  {getLevelLabel(topThree[2].level)}
                </span>
                <p className="text-xs text-gray-500 mt-2">{topThree[2].trips} putovanja</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Ranks 4-10 */}
      <div className="divide-y divide-gray-100">
        {restOfList.map((entry) => (
          <div
            key={entry.rank}
            className={`flex items-center gap-4 p-4 hover:bg-gray-50 transition-colors ${
              entry.isCurrentUser ? 'bg-brand-50 border-l-4 border-go2-pink' : ''
            }`}
          >
            {/* Rank Number */}
            <div className="w-10 text-center">
              <span className="text-lg font-bold text-gray-600">#{entry.rank}</span>
            </div>

            {/* Avatar */}
            <div className={`w-12 h-12 rounded-full ${getAvatarColor(entry.level)} border-2 flex items-center justify-center font-bold`}>
              {entry.initials}
            </div>

            {/* Name & Badge */}
            <div className="flex-1">
              <p className="font-semibold text-gray-900">{entry.name}</p>
              <div className="flex items-center gap-2 mt-1">
                <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${getLevelBadgeColor(entry.level)}`}>
                  {getLevelLabel(entry.level)}
                </span>
                <span className="text-xs text-gray-500">{entry.trips} putovanja</span>
              </div>
            </div>

            {/* Points */}
            <div className="text-right">
              <p className="text-xl font-bold text-gray-900">{entry.points.toLocaleString()}</p>
              <p className="text-xs text-gray-500">bodova</p>
            </div>

            {/* Current User Indicator */}
            {entry.isCurrentUser && (
              <div className="pl-2">
                <Star className="w-5 h-5 text-go2-pink fill-go2-pink" />
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="bg-gray-50 p-4 text-center border-t border-gray-200">
        <p className="text-sm text-gray-600">
          Tvoja pozicija: <span className="font-bold text-go2-pink">#{currentUserRank} od 247 putnika</span>
        </p>
      </div>
    </div>
  );
};
